import { Link } from 'react-router-dom'

import { CompanyMonogram, CompanySpine } from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { formatDateMedium } from '@/shared/i18n/format'
import { useI18n } from '@/shared/i18n'
import type { ProjectSummary } from '@/shared/types/domain'

import {
  barMetrics,
  missingDates,
  monthTicks,
  type GroupBy,
  type TimelineDomain,
  type TimelineGroup,
} from '../domain'
import { TimelineBar } from './TimelineBar'

/** Ancho de la columna de nombres. Fijo: los carriles tienen que alinearse. */
const LABEL_W = 'w-[260px] shrink-0'

export interface TimelineChartProps {
  groups: TimelineGroup[]
  domain: TimelineDomain
  today: string
  groupBy: GroupBy
}

export function TimelineChart({ groups, domain, today, groupBy }: TimelineChartProps) {
  const t = useT()
  const { locale } = useI18n()
  const ticks = monthTicks(domain)

  return (
    <div className="hairline overflow-hidden rounded-[var(--radius-card)] bg-surface-1">
      {/* -------------------------------------------------------------- eje */}
      <div className="hairline-b flex items-stretch bg-surface-2">
        <div className={`${LABEL_W} px-3 py-1.5 text-2xs text-text-3`}>
          {t(groupBy === 'responsable' ? 'timeline.axis.person' : 'timeline.axis.project')}
        </div>
        <div className="relative flex-1">
          {ticks.map((tick) => (
            <span
              key={tick.date}
              style={{ left: `${String(tick.leftPct)}%` }}
              className="num absolute top-1.5 -translate-x-1/2 whitespace-nowrap text-2xs text-text-3"
            >
              {formatDateMedium(tick.date, locale)}
            </span>
          ))}
        </div>
      </div>

      <div className="relative">
        {/*
          Las guías de mes y la línea de HOY van detrás de todos los carriles, en
          una sola capa absoluta. Dibujarlas por fila las multiplicaría por la
          cantidad de proyectos y, peor, permitiría que se desalinearan entre sí.
        */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex">
          <div className={LABEL_W} />
          <div className="relative flex-1">
            {ticks.map((tick) => (
              <span
                key={tick.date}
                style={{
                  left: `${String(tick.leftPct)}%`,
                  backgroundColor: 'hsl(var(--hairline))',
                }}
                className="absolute inset-y-0 w-px"
              />
            ))}
            {domain.todayPct !== null ? (
              <span
                style={{
                  left: `${String(domain.todayPct)}%`,
                  backgroundColor: 'hsl(var(--signal))',
                }}
                className="absolute inset-y-0 w-px"
              />
            ) : null}
          </div>
        </div>

        {groups.map((group) => (
          <section key={group.key}>
            <header className="hairline-b flex items-center gap-2 bg-surface-2/60 px-3 py-1">
              {group.hue !== null ? (
                <CompanySpine hue={group.hue} label={group.label} className="h-3.5" />
              ) : null}
              <h3 className="text-xs text-text-1">{group.label}</h3>
              <span className="num text-2xs text-text-3">
                {t('timeline.groupCount', { count: group.projects.length })}
              </span>
            </header>

            <ul>
              {group.projects.map((project) => (
                <TimelineRow
                  key={project.id}
                  project={project}
                  domain={domain}
                  today={today}
                  showCompany={groupBy !== 'empresa'}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}

interface TimelineRowProps {
  project: ProjectSummary
  domain: TimelineDomain
  today: string
  /** En los carriles por persona hace falta decir de qué empresa es cada cosa. */
  showCompany: boolean
}

function TimelineRow({ project, domain, today, showCompany }: TimelineRowProps) {
  const t = useT()
  const { locale } = useI18n()
  const bar = barMetrics(project, domain, today)
  const falta = missingDates(project)

  const rango =
    project.start_date && project.target_end_date
      ? `${formatDateMedium(project.start_date, locale)} → ${formatDateMedium(project.target_end_date, locale)}`
      : ''

  return (
    <li className="hairline-b flex items-stretch last:border-b-0 hover:bg-surface-2">
      <div className={`${LABEL_W} flex min-w-0 items-center gap-1.5 px-3 py-1.5`}>
        {showCompany ? (
          <CompanyMonogram name={project.company.name} hue={project.company.color_hue} />
        ) : null}
        <Link
          to={`/projects/${project.id}`}
          className="min-w-0 truncate text-xs text-text-1 hover:text-signal hover:underline"
        >
          {project.name}
        </Link>
      </div>

      <div className="relative min-h-7 flex-1">
        {bar ? (
          <TimelineBar bar={bar} label={`${project.name} · ${rango}`} />
        ) : (
          /*
            SIN FECHAS NO SE DIBUJA NADA, PERO EL PROYECTO NO DESAPARECE.
            Esconderlo convertiría un dato que falta en un proyecto que no
            existe, y el que lo mira no tendría forma de notar la diferencia.
            El cartel dice exactamente qué falta, que es además lo accionable.
          */
          <span className="absolute top-1/2 left-0 -translate-y-1/2 text-2xs text-text-3">
            {t(`timeline.missing.${falta ?? 'sin-fechas'}`)}
          </span>
        )}
      </div>
    </li>
  )
}
